// SharedDay.jsx - Public view of a shared day of sessions
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { httpRequest } from '../../utils/httpClient';

function SharedDay() {
  const { slug, date } = useParams();
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSharedData = async () => {
      if (!slug || !date) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const response = await httpRequest(`${API_BASE}/share/day/${slug}/${date}`, {
          method: 'GET',
        });

        if (response.ok) {
          const data = await response.json();
          if (!data || !Array.isArray(data.sessions)) {
            setError('Failed to load shared day');
          } else {
            setDayData(data);
          }
        } else {
          setError('Failed to load shared day');
        }
      } catch (err) {
        setError('Error loading shared day');
      } finally {
        setLoading(false);
      }
    };

    loadSharedData();
  }, [slug, date]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, white)',
        color: 'var(--text-primary, #333)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div style={{ fontSize: '1.2rem' }}>Loading shared day...</div>
        </div>
      </div>
    );
  }

  if (error || !dayData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, white)',
        color: 'var(--text-primary, #333)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
          <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Day not found</div>
          <div style={{ fontSize: '1rem', color: 'var(--text-secondary, #666)' }}>
            {error || 'This shared day may have expired or is invalid'}
          </div>
        </div>
      </div>
    );
  }

  const { display_date, username, total_sessions, sessions } = dayData;

  const winnerIds = (session) => {
    const winners = session.win_loss?.winners || session.winners || [];
    return new Set(winners.map(String));
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary, white)',
      padding: '1rem',
      fontFamily: 'var(--font-family, system-ui, -apple-system, sans-serif)'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: '1rem 0',
        textAlign: 'center',
        borderBottom: '1px solid var(--border-color, #e0e0e0)',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
          color: 'var(--text-primary, #333)',
          margin: '0 0 0.5rem 0',
          fontWeight: '600'
        }}>
          📅 Shared Gaming Day
        </h1>
        <p style={{
          fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
          color: 'var(--text-secondary, #666)',
          margin: '0 0 0.25rem 0'
        }}>
          {display_date || date}
        </p>
        <p style={{
          fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
          color: 'var(--text-secondary, #666)',
          margin: '0'
        }}>
          {username ? `${username}'s plays` : 'Shared plays'} • {total_sessions} session{total_sessions !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Sessions */}
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
      }}>
        {sessions.map((session) => {
          const winners = winnerIds(session);
          return (
            <div
              key={session.id}
              style={{
                backgroundColor: 'var(--surface-color, #fff)',
                border: '1px solid var(--border-color, #e0e0e0)',
                borderRadius: '16px',
                padding: '1.25rem',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {session.game_image && (
                  <img
                    src={session.game_image}
                    alt={session.game_name}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      backgroundColor: '#eee',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <h2 style={{
                    fontSize: '1.25rem',
                    margin: '0 0 0.25rem',
                    color: 'var(--text-primary, #333)'
                  }}>
                    {session.game_name}
                  </h2>
                  <p style={{
                    margin: 0,
                    color: 'var(--text-secondary, #666)',
                    fontSize: '0.9rem'
                  }}>
                    {session.time_display || ''}
                  </p>
                </div>
              </div>

              {Array.isArray(session.participants) && session.participants.length > 0 && (
                <div>
                  <p style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary, #666)',
                    margin: '0 0 0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Players
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    {session.participants.map((participant, index) => {
                      const id = participant.user_id !== undefined ? String(participant.user_id) : String(index);
                      const isWinner = winners.has(id);
                      const name = participant.username || participant.name || participant.display_name || 'Player';
                      const score = participant.score;
                      return (
                        <li
                          key={`${session.id}-${id}-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            backgroundColor: isWinner ? 'rgba(46, 125, 50, 0.08)' : 'var(--bg-secondary, #f7f7f7)',
                            color: isWinner ? 'var(--pista-green-primary, #2E7D32)' : 'var(--text-primary, #333)',
                            fontWeight: isWinner ? 600 : 400,
                          }}
                        >
                          <span>
                            {isWinner && '🏆 '}
                            {name}
                          </span>
                          {score !== undefined && score !== null && score !== '' && (
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{score}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        maxWidth: '720px',
        margin: '2rem auto 0',
        padding: '1rem',
        textAlign: 'center',
        borderTop: '1px solid var(--border-color, #e0e0e0)',
        fontSize: '0.9rem',
        color: 'var(--text-secondary, #666)'
      }}>
        <p style={{ margin: '0' }}>
          Shared via Pista •
          <Link
            to="/"
            style={{
              color: 'var(--pista-green-primary, #2E7D32)',
              textDecoration: 'none',
              marginLeft: '0.5rem'
            }}
          >
            Create your own gaming stats →
          </Link>
        </p>
      </div>
    </div>
  );
}

export default SharedDay;
